import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Share, Edit } from 'lucide-react';

interface HistoryItemMenuProps {
  chatId: number;
  chatTitle?: string;
  isFavorited?: boolean;
  onToggleFavorite: (chatId: number) => void;
  onDelete: (chatId: number) => void;
  onShare?: (chatId: number) => void;
  onRenameTitle?: (chatId: number) => void;
}

const HistoryItemMenu: React.FC<HistoryItemMenuProps> = ({
  chatId,
  chatTitle,
  isFavorited = false,
  onToggleFavorite,
  onDelete,
  onShare,
  onRenameTitle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // メニュー外クリック検知
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // ボタンまたはメニュー内のクリックは無視
      if (
        buttonRef.current?.contains(event.target as Node) ||
        menuRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleFavorite = (e: React.MouseEvent) => {
    console.log('お気に入り切り替えクリック:', { chatId, isFavorited });
    e.stopPropagation();
    e.preventDefault();
    onToggleFavorite(chatId);
    setIsOpen(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    console.log('削除クリック:', { chatId });
    e.stopPropagation();
    e.preventDefault();
    onDelete(chatId);
    setIsOpen(false);
  };

  const handleShare = (e: React.MouseEvent) => {
    console.log('共有クリック:', { chatId });
    e.stopPropagation();
    e.preventDefault();
    if (onShare) {
      onShare(chatId);
    }
    setIsOpen(false);
  };

  const handleRenameTitle = (e: React.MouseEvent) => {
    console.log('タイトル変更クリック:', { chatId });
    e.stopPropagation();
    e.preventDefault();
    if (onRenameTitle) {
      onRenameTitle(chatId);
    }
    setIsOpen(false);
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    console.log('メニューボタンクリック:', { chatId, currentIsOpen: isOpen });
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* …アイコンボタン */}
      <button
        ref={buttonRef}
        onClick={handleMenuClick}
        className="p-1.5 bg-black/10 hover:bg-black/20 rounded transition-all duration-200"
        aria-label="メニューを開く"
      >
        <MoreVertical className="w-4 h-4 text-gray-700" />
      </button>

      {/* ドロップダウンメニュー */}
      {isOpen && (
        <div
          ref={menuRef}
          className="absolute top-full right-0 mt-1 z-[9999] bg-white rounded-lg shadow-lg border border-[#E2DFD6] py-1 w-max"
        >
          {/* お気に入りに登録/解除 */}
          <button
            onClick={handleToggleFavorite}
            className="block w-full px-2 py-2 text-left text-sm text-gray-700 hover:bg-[#F0EDE4] transition-colors whitespace-nowrap"
          >
            {isFavorited ? 'お気に入りから解除' : 'お気に入りに登録'}
          </button>

          {/* 共有 */}
          {onShare && (
            <button
              onClick={handleShare}
              className="flex items-center gap-2 w-full px-2 py-2 text-left text-sm text-gray-700 hover:bg-[#F0EDE4] transition-colors whitespace-nowrap"
            >
              <Share className="w-4 h-4" />
              共有
            </button>
          )}

          {/* タイトルの変更 */}
          {onRenameTitle && (
            <button
              onClick={handleRenameTitle}
              className="flex items-center gap-2 w-full px-2 py-2 text-left text-sm text-gray-700 hover:bg-[#F0EDE4] transition-colors whitespace-nowrap"
            >
              <Edit className="w-4 h-4" />
              タイトルの変更
            </button>
          )}

          {/* 削除する */}
          <button
            onClick={handleDelete}
            className="block w-full px-2 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
          >
            削除する
          </button>
        </div>
      )}
    </>
  );
};

export default HistoryItemMenu;

